import { documentQuery } from "@common/api"
import { LoadingState } from "@common/components/shared/loading-state"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { Suspense, useMemo } from "react"
import { DocumentViewer } from "@/components/document/document-viewer"
import { decodeSource, sourceSearchSchema } from "@/lib/source-evidence"
import { DocumentReferencesBar } from "./-components/document-references-bar"
import { DownloadDocumentButton } from "./-components/download-document-button"

export const Route = createFileRoute("/_layout/documents/$documentId")({
  validateSearch: (search) => sourceSearchSchema.parse(search),
  loaderDeps: ({ search: { page, source } }) => ({ page, source }),
  loader: async ({
    context: { queryClient },
    params: { documentId },
    deps,
  }) => {
    const detail = await queryClient.ensureQueryData(
      documentQuery({ document_id: documentId }),
    )
    if (detail.active_attributes?.archetype === "airworthiness_directive") {
      throw redirect({
        to: "/airworthiness-directives/$documentId",
        params: { documentId },
        search: deps,
        replace: true,
      })
    }
    return { breadcrumb: detail.file_name }
  },
  component: DocumentViewerPage,
})

function DocumentViewerPage(): React.ReactElement {
  const { documentId } = Route.useParams()
  const { page, source } = Route.useSearch()
  const decoded = useMemo(() => decodeSource(source), [source])
  const { data: detail } = useSuspenseQuery(
    documentQuery({ document_id: documentId }),
  )

  return (
    <div className="flex h-full flex-col">
      <DocumentReferencesBar links={detail.links ?? []} />
      <div className="min-h-0 flex-1">
        <Suspense fallback={<LoadingState />}>
          <DocumentViewer
            documentId={documentId}
            page={page}
            sourceEvidence={decoded.evidence}
            activeHighlights={decoded.boxes}
            action={
              <DownloadDocumentButton
                documentId={documentId}
                fileName={detail.file_name}
              />
            }
          />
        </Suspense>
      </div>
    </div>
  )
}
